import {
  ModalForm,
  type ProFormInstance,
  ProFormText,
} from '@ant-design/pro-components';
import { useIntl } from '@umijs/max';
import { message } from 'antd';
import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { useImmer } from 'use-immer';
import { changePwd } from '@/api/user/login/service';
import { formRules } from '@/utils/formRules';

export interface ChangePwdRef {
  open: () => void;
}

interface ChangePwdProps {
  /**
   * 改密成功后的回调。由 AvatarDropdown 传入完整登出流程
   * (通知后端登出 → 清 token → 清多页签缓存 → 回登录页),
   * 改密后旧会话不得继续使用。
   */
  onSuccess?: () => void;
}

const ChangePwdModal = forwardRef<ChangePwdRef, ChangePwdProps>(
  ({ onSuccess }, ref) => {
    const intl = useIntl();
    const [visible, setVisible] = useImmer<boolean>(false);
    const formRef = useRef<ProFormInstance>(null);
    useImperativeHandle(ref, () => ({
      open: () => setVisible(true),
    }));

    return (
      <ModalForm
        formRef={formRef}
        width={460}
        modalProps={{
          destroyOnClose: true,
          maskClosable: false,
          onCancel: () => {
            setVisible(false);
          },
        }}
        onFinish={async (values) => {
          await changePwd(
            values as {
              oldPassword: string;
              newPassword: string;
              confirmPassword: string;
            },
          );
          message.success(
            intl.formatMessage({ id: 'globalHeader.changePwd.success' }),
          );
          setVisible(false);
          onSuccess?.();
        }}
        layout="horizontal"
        title={intl.formatMessage({ id: 'globalHeader.changePwd.title' })}
        open={visible}
        omitNil={false}
      >
        <ProFormText.Password
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                id: 'globalHeader.changePwd.oldPassword.required',
              }),
            },
          ]}
          name="oldPassword"
          label={intl.formatMessage({
            id: 'globalHeader.changePwd.oldPassword',
          })}
        />
        <ProFormText.Password
          dependencies={['oldPassword']}
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                id: 'globalHeader.changePwd.newPassword.required',
              }),
            },
            formRules.password(),
            ({ getFieldValue }) => ({
              validator: (_, value) =>
                value && value === getFieldValue('oldPassword')
                  ? Promise.reject(
                      new Error(
                        intl.formatMessage({
                          id: 'globalHeader.changePwd.newPassword.sameAsOld',
                        }),
                      ),
                    )
                  : Promise.resolve(),
            }),
          ]}
          name="newPassword"
          label={intl.formatMessage({
            id: 'globalHeader.changePwd.newPassword',
          })}
        />
        <ProFormText.Password
          dependencies={['newPassword']}
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                id: 'globalHeader.changePwd.confirmPassword.required',
              }),
            },
            ({ getFieldValue }) => ({
              validator: (_, value) =>
                value && value !== getFieldValue('newPassword')
                  ? Promise.reject(
                      new Error(
                        intl.formatMessage({
                          id: 'globalHeader.changePwd.confirmPassword.mismatch',
                        }),
                      ),
                    )
                  : Promise.resolve(),
            }),
          ]}
          name="confirmPassword"
          label={intl.formatMessage({
            id: 'globalHeader.changePwd.confirmPassword',
          })}
        />
      </ModalForm>
    );
  },
);

export default ChangePwdModal;
